import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { ApiBaseService } from '../../../../../projects/vendors/src/lib/services/api-base.service';
import { VendorsService } from '../../../../../projects/vendors/src/lib/vendors.service';
import { ApiEnum } from '../../../@application/enum/api.enum';
import { AppNotification } from '../../../@domain/notification';

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
  standalone: false
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  private apiBaseService = inject(ApiBaseService);
  private vendorsService = inject(VendorsService);
  private subscription$ = new Subscription();

  notifications: AppNotification[] = [];
  popOverVisible = false;
  showAll = false;

  get unreadCount(): number {
    return this.notifications.filter(n => !n.isRead).length;
  }

  get todayNotifications(): AppNotification[] {
    return this.notifications.filter(n => this.isToday(n.createdAt));
  }

  get olderNotifications(): AppNotification[] {
    const older = this.notifications.filter(n => !this.isToday(n.createdAt));
    return this.showAll ? older : older.slice(0, 3);
  }

  get hasMoreNotifications(): boolean {
    const olderCount = this.notifications.filter(n => !this.isToday(n.createdAt)).length;
    return !this.showAll && olderCount > 3;
  }

  private isToday(dateStr: string): boolean {
    const date = new Date(dateStr);
    const now = new Date();
    return date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
  }

  ngOnInit(): void {
    this.fetchNotifications();

    this.subscription$.add(
      this.vendorsService.qrOrderRequestSubject.subscribe({
        next: value => {
          if (value) this.fetchNotifications();
        }
      })
    );

    this.subscription$.add(
      this.vendorsService.newOrderSubject.subscribe({
        next: value => {
          if (value) this.fetchNotifications();
        }
      })
    );

    this.subscription$.add(
      this.vendorsService.reservationNotificationSubject.subscribe({
        next: value => {
          if (value) this.fetchNotifications();
        }
      })
    );
  }

  onOpen(visible: boolean): void {
    if (visible) {
      this.fetchNotifications();
    }
  }

  onClosePopOver(): void {
    this.popOverVisible = !this.popOverVisible;
  }

  markAllRead(): void {
    this.notifications = this.notifications.map(n => ({ ...n, isRead: true }));
  }

  toggleShowAll(): void {
    this.showAll = true;
  }

  getTimeDisplay(createdAt: string): string {
    const date = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHour < 24) return `${diffHour} hr ago`;

    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    }) + ', ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  getNotificationIcon(type: string): string {
    switch (type) {
      case 'QR_ORDER_REQUEST':
        return 'qrcode';
      case 'ORDER_STATUS':
        return 'shopping-cart';
      case 'REPORT_READY':
        return 'file-text';
      case 'RESERVATION_REQUEST':
        return 'calendar';
      default:
        return 'team';
    }
  }

  private fetchNotifications(): void {
    const params = new HttpParams().set('unreadOnly', 'false');
    this.apiBaseService.GET<AppNotification[]>(
      [ApiEnum.RestaurantModule, ApiEnum.Notification, ApiEnum.Tenant],
      false, true, params
    ).subscribe({
      next: data => {
        this.notifications = (data || []).sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }
}
